import { ReactNode } from 'react';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  createBaseThemeOptions,
  palettes,
} from '@backstage/theme';

const NAVIGATION_INDICATOR = '#FFA726';

const componentOverrides = {
  MuiTextField: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiFormControl: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiSelect: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiButton: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiChip: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiIconButton: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiToggleButton: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  MuiToggleButtonGroup: {
    defaultProps: {
      size: 'small' as const,
    },
  },
  // @backstage/core-components' <Select> renders a hardcoded BootstrapInput
  // that ignores MUI's `size` prop, so its padding must be overridden directly.
  BackstageSelectInputBase: {
    styleOverrides: {
      input: ({ theme }: { theme: any }) => ({
        padding: theme.spacing(0.5, 3.25, 0.5, 1.5),
      }),
    },
  },
  // Width and gutters of every standalone page (core-components <Content>),
  // mirroring @backstage/ui's <Container> so they line up with entity pages:
  // 120rem max, centred, 16px side padding and 20px from 640px wide. The
  // vertical padding is Content's own. Content's base rule steps its sides to
  // 24px at the same 600px media query, so that key is overridden too.
  BackstageContent: {
    styleOverrides: {
      root: {
        width: '100%',
        maxWidth: '120rem',
        marginInline: 'auto',
        paddingLeft: 16,
        paddingRight: 16,
        '@media (min-width:600px)': { paddingLeft: 16, paddingRight: 16 },
        '@media (min-width:640px)': { paddingLeft: 20, paddingRight: 20 },
      },
    },
  },
};

export const lightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      navigation: {
        ...palettes.light.navigation,
        background: '#202733',
        indicator: NAVIGATION_INDICATOR,
      },
    },
  }),
  components: componentOverrides,
});

export const darkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      navigation: {
        ...palettes.dark.navigation,
        indicator: NAVIGATION_INDICATOR,
      },
    },
  }),
  components: componentOverrides,
});

export const CustomLightThemeProvider = ({
  children,
}: {
  children: ReactNode;
}) => <UnifiedThemeProvider theme={lightTheme} children={children} />;

export const CustomDarkThemeProvider = ({
  children,
}: {
  children: ReactNode;
}) => <UnifiedThemeProvider theme={darkTheme} children={children} />;
